'use client';

import { Box, Button, Stack, Typography } from '@/primitives';
import { AnimalSexBadge } from '@/hooks/publicSearch/animal/helpers';
import { ANIMAL_SEX } from '@/components/animals/animalSex';
import { MATING_DISPLAY_BY } from '../types';
import type { MatingDisplayBy } from '../types';

interface MatingResultsPaginationProps {
  displayBy: MatingDisplayBy;
  safePageIndex: number;
  totalPages: number;
  currentPrimaryDisplayLabel: string;
  onPageChange: (index: number) => void;
}

/** Previous / current sire-or-dam label / Next controls for paging through grouped mating results. */
export default function MatingResultsPagination({
  displayBy,
  safePageIndex,
  totalPages,
  currentPrimaryDisplayLabel,
  onPageChange,
}: MatingResultsPaginationProps) {
  const primaryLabel = displayBy === MATING_DISPLAY_BY.SIRE ? 'Sire' : 'Dam';

  return (
    <Stack
      direction="row"
      useFlexGap
      sx={{
        alignItems: 'center',
        justifyContent: totalPages > 1 ? 'space-between' : 'center',
        flexWrap: { xs: 'nowrap', md: 'wrap' },
        mb: 1.5,
        flexShrink: 0,
        gap: 1,
        width: '100%',
      }}
    >
      {totalPages > 1 && (
        <Button
          variant="outlined"
          size="small"
          disabled={safePageIndex <= 0}
          onClick={() => onPageChange(safePageIndex - 1)}
          sx={{ flexShrink: 0, textTransform: 'none', minWidth: 88 }}
        >
          Previous
        </Button>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: {
            xs: totalPages > 1 ? 'flex-start' : 'center',
            md: 'center',
          },
          gap: 0.75,
          minWidth: 0,
          flex: '1 1 auto',
          maxWidth: { xs: '100%', md: 'none' },
        }}
      >
        <AnimalSexBadge
          sex={
            displayBy === MATING_DISPLAY_BY.SIRE
              ? ANIMAL_SEX.MALE
              : ANIMAL_SEX.FEMALE
          }
        />
        <Typography
          variant="bodymedium"
          sx={{
            fontSize: '0.875rem',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: { xs: 'unset', md: 'ellipsis' },
            whiteSpace: { xs: 'normal', md: 'nowrap' },
          }}
        >
          {totalPages > 1 ? (
            <>
              {primaryLabel} {safePageIndex + 1} of {totalPages}
              {' · '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {currentPrimaryDisplayLabel}
              </Box>
            </>
          ) : (
            <>
              {primaryLabel}:{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {currentPrimaryDisplayLabel}
              </Box>
            </>
          )}
        </Typography>
      </Box>
      {totalPages > 1 && (
        <Button
          variant="outlined"
          size="small"
          disabled={safePageIndex >= totalPages - 1}
          onClick={() => onPageChange(safePageIndex + 1)}
          sx={{ flexShrink: 0, textTransform: 'none', minWidth: 88 }}
        >
          Next
        </Button>
      )}
    </Stack>
  );
}
